import { parseApproximateLocation } from './approximate-location';
import { formatInTimeZone } from 'date-fns-tz';
import type { EvidensiaClinic, EvidensiaTreatment } from './evidensia-clinics';

const FRESH_FOR_MS = 24 * 60 * 60 * 1000;
// Allow one more day as fallback if the daily refresh fails.
const MAX_AGE_MS = 48 * 60 * 60 * 1000;
const storageKey = (treatment: EvidensiaTreatment) => `evidensia-clinics:v1:${treatment}`;
type CacheEntry = { updatedAt: number; clinics: EvidensiaClinic[] };
type CacheStorage = Pick<Storage, 'getItem' | 'setItem'>;

function isUrl(value: unknown, hostname: string) {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      url.hostname === hostname &&
      !url.port &&
      !url.username &&
      !url.password
    );
  } catch {
    return false;
  }
}

function isOpeningHours(value: unknown): value is EvidensiaClinic['openingHours'] {
  if (value === null) return true;
  if (!value || typeof value !== 'object') return false;
  const hours = value as Record<string, unknown>;
  const validTime = (time: unknown) =>
    time === null || (typeof time === 'string' && Number.isFinite(Date.parse(time)));
  return (
    typeof hours.date === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(hours.date) &&
    typeof hours.allDay === 'boolean' &&
    typeof hours.closed === 'boolean' &&
    validTime(hours.opensAt) &&
    validTime(hours.closesAt)
  );
}

function isClinics(value: unknown): value is EvidensiaClinic[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((clinic) => {
      if (!clinic || typeof clinic !== 'object') return false;
      return (
        Number.isInteger(clinic.id) &&
        clinic.id > 0 &&
        parseApproximateLocation(clinic) !== null &&
        typeof clinic.name === 'string' &&
        clinic.name.trim() !== '' &&
        typeof clinic.address === 'string' &&
        typeof clinic.city === 'string' &&
        isUrl(clinic.url, 'evidensia.se') &&
        (clinic.imageUrl === null || isUrl(clinic.imageUrl, 'evidensia.se')) &&
        (clinic.bookingUrl === null || isUrl(clinic.bookingUrl, 'app.evidensia.se')) &&
        (clinic.phone === null || typeof clinic.phone === 'string') &&
        (clinic.phoneInfo === null || typeof clinic.phoneInfo === 'string') &&
        isOpeningHours(clinic.openingHours)
      );
    })
  );
}

export function createEvidensiaClinicsCache({
  fetchFn = fetch,
  getStorage = () => (typeof window === 'undefined' ? null : window.localStorage),
  now = Date.now,
  timeoutInMs = 10_000,
}: {
  fetchFn?: typeof fetch;
  getStorage?: () => CacheStorage | null;
  now?: () => number;
  timeoutInMs?: number;
} = {}) {
  const entries = new Map<EvidensiaTreatment, CacheEntry>();
  const requests = new Map<EvidensiaTreatment, Promise<EvidensiaClinic[] | null>>();

  function read(treatment: EvidensiaTreatment): CacheEntry | null {
    let entry = entries.get(treatment);
    if (!entry) {
      try {
        const stored = JSON.parse(getStorage()?.getItem(storageKey(treatment)) ?? 'null');
        if (
          stored &&
          typeof stored.updatedAt === 'number' &&
          Number.isFinite(stored.updatedAt) &&
          isClinics(stored.clinics)
        )
          entry = stored;
      } catch {
        // Corrupt or blocked storage must not prevent fetching clinics.
      }
    }
    if (!entry) return null;
    const age = now() - entry.updatedAt;
    if (age < 0 || age >= MAX_AGE_MS) return null;
    entries.set(treatment, entry);
    return entry;
  }

  function get(treatment: EvidensiaTreatment) {
    return read(treatment)?.clinics ?? null;
  }

  function load(treatment: EvidensiaTreatment): Promise<EvidensiaClinic[] | null> {
    const cached = read(treatment);
    const currentTime = now();
    // Keep the clinic list cached, but today's opening hours expire at midnight.
    if (
      cached &&
      currentTime - cached.updatedAt < FRESH_FOR_MS &&
      formatInTimeZone(cached.updatedAt, 'Europe/Stockholm', 'yyyy-MM-dd') ===
        formatInTimeZone(currentTime, 'Europe/Stockholm', 'yyyy-MM-dd')
    )
      return Promise.resolve(cached.clinics);
    const pending = requests.get(treatment);
    if (pending) return pending;

    const promise = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutInMs);
      try {
        const response = await fetchFn(`/api/evidensia-clinics?treatment=${treatment}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Clinics unavailable');
        const data = await response.json();
        if (!isClinics(data?.clinics)) throw new Error('Invalid clinics response');
        const entry = { updatedAt: now(), clinics: data.clinics };
        entries.set(treatment, entry);
        try {
          getStorage()?.setItem(storageKey(treatment), JSON.stringify(entry));
        } catch {
          // Keep the in-memory result when browser storage is unavailable.
        }
        return entry.clinics;
      } catch {
        return get(treatment);
      } finally {
        clearTimeout(timer);
      }
    })().finally(() => {
      if (requests.get(treatment) === promise) requests.delete(treatment);
    });
    requests.set(treatment, promise);
    return promise;
  }

  return { get, load };
}

export const evidensiaClinicsCache = createEvidensiaClinicsCache();
